import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as EyeIcon } from '../../../../assets/icons/eye-fill-icon.svg';
import { useAppBranding } from '../../../../config/branding';
import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  ApiRequestPendingInterface,
  RequestPendingInterface,
} from '../../../../interface/request-pending-interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationDetails } from '../../../../redux/slice/NotificationSlice';
import {
  disablePendingFilterApplied,
  fetchRequestPending,
  fetchRequestPendingFilters,
  updatePendingFilters,
  setSortData,
} from '../../../../redux/slice/RequestPendingSlice';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import SuccessNotification from '../../../../shared-components/success-notification/SuccessNotification';
import SidePaneModel from '../../../../shared-components/table-side-panel/SidePaneModel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import penaltyManagementApi from '../../../../utils/api/penalty-management';

import ViewOverrides from './ViewOverrides';

interface ModelDetailsInterface {
  title: string;
  header: boolean;
  content: JSX.Element;
  rightCorner: string;
  notification: boolean;
  position?: string;
}

/**
 * Request pending component
 * @returns
 */
function RequestPending() {
  const { t } = useTranslation('penaltyManagement');
  const dispatch = useAppDispatch();
  const branding = useAppBranding();
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [isSidePaneOpen, setIsSidePaneOpen] = useState<boolean>(false);
  const [SidePaneView, setSidePaneView] = useState<ModelDetailsInterface>({
    title: ' ',
    header: false,
    content: <div />,
    rightCorner: '',
    notification: false,
  });
  const {
    requestPending,
    limit,
    page,
    totalPages,
    totalResults,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
    loading,
  } = useAppSelector((state) => state.requestPending);

  /**
   * function for closing modal
   * @param message
   */
  const closeSidePane = (message: string) => {
    if (message === 'success') {
      setIsSidePaneOpen(false);
      setSidePaneView({
        title: ' ',
        header: false,
        content: (
          <SuccessNotification
            title={message}
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={() => setIsSidePaneOpen(false)}
          />
        ),
        rightCorner: '',
        notification: false,
      });
    } else {
      setIsSidePaneOpen(false);
    }
  };

  /**
   * function for opening overlay
   */
  const handleOpenSidePane = (currentData: RequestPendingInterface) => {
    const accountDetails = requestPending.find((value) => value._id === currentData._id);

    if (accountDetails) {
      setIsSidePaneOpen(true);
      setSidePaneView({
        title: ' ',
        header: false,
        content: (
          <ViewOverrides
            closeModal={closeSidePane}
            currentData={accountDetails}
            type="pending"
          />
        ),
        rightCorner: '',
        notification: false,
      });
    }
  };

  /**
   * Renders custom data for the Dynamic Pricing column
   * @param {AccountListInterface} currentRow - The current row data
   * @returns {JSX.Element} The rendered dynamic pricing cell
   */
  const renderDynamicPricing = (currentRow: RequestPendingInterface) => {
    let dynamicPricingList: string;
    if (currentRow.dynamicPricing.length > 2) {
      dynamicPricingList = t('all');
    } else {
      dynamicPricingList = currentRow.dynamicPricing
        .map((item) => item.charAt(0).toUpperCase() + item.slice(1))
        .join(', ');
    }
    return <p>{dynamicPricingList}</p>;
  };
  /**
   * Renders custom data for the Ceiling Type column
   * @param {AccountListInterface} currentRow - The current row data
   * @returns {JSX.Element} The rendered ceiling type cell
   */
  const renderCeilingType = (currentRow: RequestPendingInterface) => {
    const formattedValue = currentRow.ceilingType
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/^./, (char) => char.toUpperCase());
    return <p>{formattedValue}</p>;
  };

  const headers = [
    {
      label: t('station'),
      accessorKey: 'stationNames' as keyof RequestPendingInterface,
      sortAllow: true,
    },
    {
      label: t('dynamicPricing'),
      accessorKey: 'dynamicPricing' as keyof RequestPendingInterface,
      sortAllow: false,
      customData: renderDynamicPricing,
    },
    {
      label: t('graceInterval'),
      accessorKey: 'graceInterval' as keyof RequestPendingInterface,
      sortAllow: true,
    },
    {
      label: t('maxCeilingValue'),
      accessorKey: 'ceilingValue' as keyof RequestPendingInterface,
      sortAllow: true,
      /**
       *  ToFixedValue
       * @param currentRow
       * @returns
       */
      customData: (currentRow: RequestPendingInterface) => (
        <div className="amount-in-transaction-table">
          <p className="table-row-each-data">
            {ToFixedValue(currentRow.ceilingValue)}
          </p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('ceilingType'),
      accessorKey: 'ceilingType' as keyof RequestPendingInterface,
      sortAllow: false,
      customData: renderCeilingType,
    },
    {
      label: t('effectiveDate'),
      accessorKey: 'effectiveFrom' as keyof RequestPendingInterface,
      sortAllow: true,
      /**
       * customData
       */
      customData: (customData: RequestPendingInterface) => (
        <div>
          {ConvertUTCtoDate(customData.effectiveFrom)} - {ConvertUTCtoDate(customData.effectiveTo)}
        </div>
      ),
    },
    {
      label: t('action'),
      accessorKey: 'action' as keyof RequestPendingInterface,
      /**
       * Rendering action
       * @returns
       */
      customData: (currentData: RequestPendingInterface) => (
        <div
          role="presentation"
          className="action-button"
          onClick={() => {
            handleOpenSidePane(currentData);
          }}>
          <EyeIcon />
        </div>
      ),
    },
  ];

  /**
   * Function for fetching request approved data
   * @param data
   */
  const fetchData = (data: ApiRequestPendingInterface) => {
    if (!loading) {
      dispatch(fetchRequestPending(data));
    }
  };

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      limit,
      page: DefaultApiLimit.page,
    });
  };

  /**
   * Table pagination function
   * @param value
   */
  const pageAndLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, ...value, sortField, sortBy });
  };

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('station'), accessorKey: 'stationNames' },
    { label: t('dynamicPricing'), accessorKey: 'dynamicPricing' },
    { label: t('graceInterval'), accessorKey: 'graceInterval' },
    { label: t('maxCeilingValue'), accessorKey: 'ceilingValue' },
    { label: t('ceilingType'), accessorKey: 'ceilingType' },
    { label: t('effectiveDate'), accessorKey: 'effectiveDate' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Function for exporting request pending data
   * @param type
   */
  const exportDataPDFOrCSV = (type: 'pdf' | 'csv') => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    setExportLoader(true);
    penaltyManagementApi
      .requestExport({ ...queryParameters, requestStatus: 'pending' })
      .then((response) => {
        const header = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: RequestPendingInterface, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'ceilingValue':
                return `${ToFixedValue(row.ceilingValue)} ${branding.text.currency}` || 'N/A';
              case 'effectiveDate':
                return `${ConvertUTCtoDate(row.effectiveFrom)} - ${ConvertUTCtoDate(row.effectiveTo)}`;
              default:
                return (row as any)[col?.accessorKey] || 'N/A';
            }
          }),
        );

        if (type === 'csv') {
          exportCSV(header, rows, t('requestPending'));
        } else {
          exportPDF(header, rows, t('requestPending'));
        }

        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Function for refreshing request approved data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page,
      limit,
      sortBy,
      sortField,
    });
  };

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    if (isFilterUpdated || requestPending.length === 0) {
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disablePendingFilterApplied());
    }
  }, [filters]);

  return (
    <div className="request-pending-wrap">
      <SidePaneModel
        title={SidePaneView.title}
        // header={SidePaneView.header}
        // rightCorner={SidePaneView.rightCorner}
        content={SidePaneView.content}
        closePanel={() => setIsSidePaneOpen(false)}
        open={isSidePaneOpen}
      />
      <CustomTable
        serialNo
        paginate
        header={headers}
        data={requestPending}
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        filterListLoading={filterListLoading}
        filters={filters}
        sortData={{ sortBy, sortField }}
        loading={loading}
        handleSortChange={handleSortFunction}
        currentPage={page}
        handleLimitChange={pageAndLimitChange}
        handlePageChange={pageAndLimitChange}
        exportData={requestPending}
        exportLoader={exportLoader}
        exportDataPdfOrCSV={exportDataPDFOrCSV}
        filtersToDispatch={updatePendingFilters}
        filterListToDispatch={fetchRequestPendingFilters}
        refreshRequired
        leftTopContent={<div />}
        sizeChangeButtonRequired
        hoverLabel={t('refresh')}
        refreshCurrentPages={handleRefresh}
        tableHeight="60vh"
        topHeader={false}
        onHiddenColumnsChange={setHiddenColumns}
        flexDirection
      />
    </div>
  );
}
export default RequestPending;
